import { useEffect, useState } from 'react';
import { api } from '../api';
import RestaurantBookingPanel from './RestaurantBookingPanel';

export default function StopRestaurantsPicker({
  destination,
  picked = [],
  onToggle,
  scaleForTravelers,
  travelersLabel,
  collapsedByDefault = false,
}) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(!collapsedByDefault);

  useEffect(() => {
    if (!destination?.id || !open) return;
    api.getRestaurants(destination.id)
      .then(all => {
        const subName = destination._bundleSubName || (
          (destination.city || '').includes(' + ')
            ? destination.city.split(' + ')[0].trim()
            : null
        );
        if (subName) {
          const re = new RegExp(`\\b${subName.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\b`, 'i');
          const filtered = (all || []).filter(r =>
            re.test(r.name || '') || re.test(r.neighborhood || '') || re.test(r.description || '')
          );
          setItems(filtered.length > 0 ? filtered : all || []);
        } else {
          setItems(all || []);
        }
      })
      .catch(() => setItems([]));
  }, [destination?.id, destination?._bundleSubName, destination?.city, open]);

  const isPicked = (id) => picked.some(p => p.id === id);
  const pickedCount = picked.length;
  const pickedTotal = picked.reduce((s, r) => s + (r.avg_meal_for_two || 0), 0);

  return (
    <div className="bg-navy-light rounded-2xl border border-brand-ink/10 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full p-3.5 flex items-center justify-between gap-2 text-left hover:bg-brand-ink/5 transition"
      >
        <div className="min-w-0 flex items-center gap-2.5">
          <span className="font-bold text-brand-ink truncate">{destination.city}</span>
          <span className="text-xs text-brand-ink/55 shrink-0">
            {pickedCount > 0 ? `· ${pickedCount} picked · $${scaleForTravelers ? scaleForTravelers(pickedTotal) : pickedTotal}` : '· No restaurants yet'}
          </span>
        </div>
        <span className="text-brand-ink/35 shrink-0">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="px-3.5 pb-3.5 border-t border-brand-ink/10 pt-3 space-y-2">
          {items === null && <div className="text-xs text-brand-ink/55">Loading…</div>}
          {items && items.length === 0 && <div className="text-xs text-brand-ink/55">No restaurants curated for {destination.city} yet.</div>}
          {items && items.map(r => {
            const sel = isPicked(r.id);
            const price = scaleForTravelers ? scaleForTravelers(r.avg_meal_for_two) : r.avg_meal_for_two;
            return (
              <button
                key={r.id}
                onClick={() => onToggle(r)}
                className={`w-full text-left rounded-xl border p-3 transition ${
                  sel ? 'border-gold ring-2 ring-gold/30 bg-gold/5' : 'border-brand-ink/10 hover:border-brand-ink/20'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm flex items-center gap-2">
                      <span className={`inline-flex items-center justify-center w-4 h-4 rounded border-2 text-[10px] ${sel ? 'bg-gold border-gold text-navy' : 'border-brand-ink/30'}`}>
                        {sel ? '✓' : ''}
                      </span>
                      <span>{r.name}</span>
                    </div>
                    <div className="text-[11px] text-brand-ink/65 mt-1 ml-6">{r.cuisine} · {r.category?.replace('_', ' ')} · {r.price_range}</div>
                    <div className="text-xs text-brand-ink/75 mt-1 ml-6">{r.description}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-base font-bold text-gold">${price}</div>
                    <div className="text-[10px] text-brand-ink/55">for {travelersLabel || '2 travelers'}</div>
                  </div>
                </div>
                <RestaurantBookingPanel restaurant={r} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
